import { useQuery } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import type { InstagramHealth } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath } from '@/Config/app/router/index.path';
import type { AIAssistantsData } from '@/Domain/components/TopBar/types';
import {
  CHANNEL_INSTAGRAM,
  OPEN_MODAL_QUERY_PARAM,
} from '@/UI/GettingStarted/components/onboardingOptions';

export const InstagramReconnectNotice = () => {
  const { t } = useTranslation(['inbox_v2']);
  const { scenario = '' } = useParams<{ scenario: string }>();
  const { data } = useQuery<InstagramHealth>({
    queryKey: [QueryKey.integration.instagramHealth],
  });
  const { data: aiAssistants } = useQuery<AIAssistantsData>({
    queryKey: [QueryKey.aiAssistants.get],
  });

  if (!data?.ai_replies_paused) {
    return null;
  }

  // Channels is gated on the same permission, so a Support operator would be bounced from the link.
  const canReconnect = !!aiAssistants?.current_ai_assistant?.permissions?.ai_assistant;

  return (
    <div
      className="flex items-start gap-2 px-1 py-1.5 text-xs text-foreground-subtle"
      data-testid="instagram-reconnect-notice"
    >
      <TriangleAlert className="shrink-0 mt-0.5 size-4 text-destructive" />
      {canReconnect ? (
        <Trans
          i18nKey="conversation.instagram_reconnect_info"
          t={t}
          parent="span"
          components={{
            reconnectLink: (
              <Link
                to={`/i/${scenario}/${IndexPath.channels}?${OPEN_MODAL_QUERY_PARAM}=${CHANNEL_INSTAGRAM}`}
                className="font-medium text-primary hover:underline"
                data-testid="instagram-reconnect-link"
              />
            ),
          }}
        />
      ) : (
        <span>{t('conversation.instagram_reconnect_ask_admin_info')}</span>
      )}
    </div>
  );
};
